import { useQuery } from '@tanstack/react-query'
import { createStore } from 'effector'
import { useUnit } from 'effector-react'
import { MenuItem } from 'react-aria-components'

import { css, cx } from '@portal/styled-system/css'

import { newsAPI } from '@/shared/api'
import { env } from '@/shared/config'
import { useI18n } from '@/shared/lib/i18n'
import { persist } from '@/shared/lib/persist/local'

import { NavbarItem } from '../navbar-item/view'
import { navbarMenu } from '../styles/recipe'
import type { NavbarItemVariant } from '../types'

const $newsLastViewTime = createStore<string | null>(null)

persist({
    store: $newsLastViewTime,
    sync: true,
    key: env.NEWS_LAST_VIEW_COOKIE_NAME,
})

function useNewPostsAvailable(): boolean {
    const { data } = useQuery({
        queryKey: ['fetchLatestPublishTime'],
        queryFn: () => newsAPI.latestPublishTime(),
        staleTime: 1000 * 60 * 10, // 10 minutes
        retry: false,
        retryOnMount: false,
    })

    const lastReadTime = useUnit($newsLastViewTime)

    if (!data) {
        return false
    }

    if (!lastReadTime) {
        return true
    }

    return Number(lastReadTime) < data.publish_time
}

export function NewsButton({ variant = 'default' }: { variant?: NavbarItemVariant }) {
    const { i18n } = useI18n()
    const classes = navbarMenu()

    const newPostsAvailable = useNewPostsAvailable()

    if (variant === 'default') {
        return (
            <NavbarItem navbarItemType="link" href="/news" styles={css.raw({ display: { base: 'none', lg: 'flex' } })}>
                {i18n.BUTTON_NEWS}
                {newPostsAvailable && (
                    <span id="news-post-bell">
                        &nbsp;
                        <i className={cx('fa-solid fa-bell', css({ color: 'green' }))} />
                    </span>
                )}
            </NavbarItem>
        )
    }

    return (
        <MenuItem href="/news" target="_blank" className={cx(classes.itemNav, css({ textTransform: 'uppercase' }))}>
            <i className={cx('fa-light fa-newspaper', classes.icon)} />
            &nbsp;{i18n.BUTTON_NEWS}
            {newPostsAvailable && (
                <span id="news-post-bell">
                    &nbsp;
                    <i className={cx('fa-solid fa-bell', classes.iconPrimary)} />
                </span>
            )}
        </MenuItem>
    )
}
